// The Kit tab draws the missing-equipment checklist, and beneath it the gear the character
// arrived with. The checklist is a checklist and never arithmetic - GURPS states the
// penalty for working without a tool in prose, and no line here moves a level for one.

import { dollars_label, type Panel, type PanelOwned, type PanelSkill } from "../vendor/web";
import { charged, type CartLine } from "./cart";
import type { KitEntry } from "./catalogue";
import { stallFor, type Stall } from "./quality";

export interface KitActions {
  setEquipped(id: string, equipped: boolean): void;
  add(item: string): void;
}

/** Owned covers both halves of ownership: the arrived sheet and the cart. */
export type KitState = "owned" | "absent" | "unserved";

export interface KitLine {
  skill: PanelSkill;
  state: KitState;
  /** What satisfies the line, on an owned one. */
  held: string | null;
  /** The items this character can buy from the line, on an absent one. */
  buyable: Stall[];
}

export function kitTab(
  panel: Panel,
  table: KitEntry[],
  stalls: Stall[],
  lines: CartLine[],
  actions: KitActions,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "kit-tab";
  wrapper.append(checklistView(checklist(panel, table, stalls, lines), actions));
  wrapper.append(kitList(panel, actions));
  return wrapper;
}

/**
 * One line per kit-table row this character has the skill for. A skill the table says
 * nothing about is silently absent, which is the failure mode the table accepts.
 */
export function checklist(
  panel: Panel,
  table: KitEntry[],
  stalls: Stall[],
  lines: CartLine[],
): KitLine[] {
  const arrived = new Map<string, string>();
  for (const row of panel.owned) {
    if (row.catalogue_id !== undefined) arrived.set(row.catalogue_id, row.name);
  }
  const carted = new Set(lines.map((line) => line.item));

  const out: KitLine[] = [];
  for (const entry of table) {
    const skill = panel.skills.find((held) => held.name === entry.skill);
    if (skill === undefined) continue;
    if (entry.items.length === 0) {
      out.push({ skill, state: "unserved", held: null, buyable: [] });
      continue;
    }
    const held = entry.items
      .map((item) => nameOf(item.id, arrived, carted, stalls))
      .find((name) => name !== null);
    if (held !== undefined && held !== null) {
      out.push({ skill, state: "owned", held, buyable: [] });
      continue;
    }
    const buyable = entry.items
      .map((item) => stallFor(stalls, item.id))
      .filter((stall): stall is Stall => stall !== undefined);
    out.push({ skill, state: "absent", held: null, buyable });
  }
  return out;
}

function nameOf(
  id: string,
  arrived: Map<string, string>,
  carted: Set<string>,
  stalls: Stall[],
): string | null {
  const owned = arrived.get(id);
  if (owned !== undefined) return owned;
  if (!carted.has(id)) return null;
  return stallFor(stalls, id)?.stock.description ?? id;
}

function checklistView(checklist: KitLine[], actions: KitActions): HTMLElement {
  const list = document.createElement("ul");
  list.className = "kit-check";
  for (const line of checklist) {
    list.append(checkRow(line, actions));
  }
  return list;
}

function checkRow(line: KitLine, actions: KitActions): HTMLElement {
  const item = document.createElement("li");
  item.className = `kit-need kit-need-${line.state}`;
  item.dataset["skill"] = line.skill.name;
  item.dataset["state"] = line.state;

  item.append(
    span("kit-skill", `${line.skill.name} ${line.skill.level}`),
    span("kit-state", reading(line)),
  );
  if (line.skill.bonus !== 0) {
    item.append(span("kit-bonus", `${signed(line.skill.bonus)} from the kit in the cart`));
  }
  if (line.buyable.length > 0) {
    const buy = document.createElement("div");
    buy.className = "kit-buy";
    for (const stall of line.buyable) buy.append(buyButton(stall, actions));
    item.append(buy);
  }
  return item;
}

/** An empty item list is a note, not a nag: nothing in the catalogue serves the skill. */
function reading(line: KitLine): string {
  if (line.state === "unserved") return "nothing sold for it";
  if (line.state === "absent") return "absent";
  return line.held === null ? "owned" : `owned: ${line.held}`;
}

function buyButton(stall: Stall, actions: KitActions): HTMLElement {
  const button = document.createElement("button");
  button.className = "kit-add";
  button.dataset["item"] = stall.item;
  button.textContent = `${stall.stock.description} ${dollars_label(charged(stall.stock, 1))}`;
  button.addEventListener("click", () => actions.add(stall.item));
  return button;
}

function signed(amount: number): string {
  return amount > 0 ? `+${amount}` : String(amount);
}

function span(className: string, text: string): HTMLElement {
  const element = document.createElement("span");
  element.className = className;
  element.textContent = text;
  return element;
}

// The gear the character arrived with. Where a shop row prints a price, one of these prints
// the word `owned`, because nothing on the arrived sheet was ever charged.
export function kitList(panel: Panel, actions: KitActions): HTMLElement {
  const list = document.createElement("ul");
  list.className = "kit";
  for (const row of panel.owned) {
    list.append(kitRow(row, actions));
  }
  return list;
}

function kitRow(row: PanelOwned, actions: KitActions): HTMLElement {
  const item = document.createElement("li");
  item.className = row.equipped ? "kit-row" : "kit-row kit-row-home";
  item.dataset["owned"] = row.id;
  item.dataset["depth"] = String(row.depth);
  item.style.marginLeft = `${row.depth * 0.9}rem`;

  const name = document.createElement("span");
  name.className = "kit-name";
  name.textContent = row.name;

  const weight = document.createElement("span");
  weight.className = "kit-weight";
  weight.textContent = row.weight;

  const price = document.createElement("span");
  price.className = "kit-price";
  price.textContent = "owned";

  item.append(name, weight, price);
  if (row.can_unequip) item.append(equippedBox(row, actions));
  return item;
}

function equippedBox(row: PanelOwned, actions: KitActions): HTMLElement {
  const box = document.createElement("button");
  box.className = row.equipped ? "kit-equipped kit-equipped-on" : "kit-equipped";
  box.setAttribute("role", "checkbox");
  box.setAttribute("aria-checked", String(row.equipped));
  box.setAttribute("aria-label", `${row.name} carried`);
  box.textContent = row.equipped ? "Carried" : "At home";
  box.addEventListener("click", () => actions.setEquipped(row.id, !row.equipped));
  return box;
}
